import { ChevronLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import classnames from "classnames";
import StatusChip from "@components/ui-components/status-chip";
import { ComponentSpinner } from "@components/spinner";
import useBrandSummary from "./use-brand-summary";
import useActiveTab from "./hooks/use-active-tab";
import { getBrandDetailTabs } from "./tabs-config";
import Overview from "./overview";
import BrandKyc from "./kyc";
import BrandContract from "./contract";
import Publishers from "./publishers";
import DebitNotes from "./debit-notes";
import PaymentHistory from "./payment-history";
import { KYC_LABEL, KYC_TONE, FREQUENCY_LABEL, PAYMENT_MODEL_LABEL  } from "@src/utility/constants/panel/common";

const BrandDetails = () => {
    
    const { id } = useParams();
    const { brand, loading, isSelfPayout, refetch } = useBrandSummary(id);
    const tabs = getBrandDetailTabs({ debitNotes: !isSelfPayout });
    const { activeTab, setActiveTab } = useActiveTab(tabs);

    if (loading) {
        return (
            <div className="flex justify-center py-24">
                <ComponentSpinner />
            </div>
        );
    }

    if (!brand) return null;

    const tabCounts = { publishers: brand.publisher_count };

    return (
        <div className="p-6">
            <Link to="/brands" className="inline-flex items-center gap-1 text-sm text-muted hover:text-gray-700">
                <ChevronLeft className="h-4 w-4" />
                Brands
            </Link>

            <div className="mt-2 flex items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-tight">{brand.name}</h1>
                <StatusChip tone={KYC_TONE[brand.kyc_status] || "neutral"} label={KYC_LABEL[brand.kyc_status] || brand.kyc_status} />
            </div>
            <p className="mt-1 text-sm text-muted">
                {brand.platform_name ? brand.platform_name.charAt(0).toUpperCase() + brand.platform_name.slice(1) : "—"}
                {" · "}{PAYMENT_MODEL_LABEL[brand.payment_model] || brand.payment_model}
                {/* Debit-note frequency is a managed-payout setting — a self-payout
                    brand never has one raised against it. */}
                {!isSelfPayout && <>{" · "}{FREQUENCY_LABEL[brand.debit_note_frequency] || brand.debit_note_frequency} debit notes</>}
                {" · "}KYC window {brand.kyc_window ?? "—"} days
            </p>

            <div className="mt-5 inline-flex rounded-lg bg-light p-1">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={classnames(
                            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                            activeTab === tab.id ? "bg-white text-gray-900 shadow-sm" : "text-muted hover:text-gray-700"
                        )}>
                        {tab.label}
                        {Number.isFinite(tabCounts[tab.id]) && ` (${tabCounts[tab.id]})`}
                    </button>
                ))}
            </div>

            <div className="mt-6">
                {activeTab === "overview" && <Overview brandId={id} isSelfPayout={isSelfPayout} onSaved={refetch} />}
                {activeTab === "kyc" && <BrandKyc brandId={id} />}
                {activeTab === "contract" && <BrandContract brandId={id} brandName={brand.name} />}
                {activeTab === "publishers" && <Publishers brandId={id} brandName={brand.name} isSelfPayout={isSelfPayout} />}
                {activeTab === "debit-notes" && !isSelfPayout && <DebitNotes brandId={id} brand={brand} />}
                {activeTab === "payment-history" && <PaymentHistory brandId={id} isSelfPayout={isSelfPayout} />}
            </div>
        </div>
    );
};

export default BrandDetails;
